<script setup lang="ts">
// icons
import { faArrowUp, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faCube, faSearchPlus } from '@fortawesome/pro-light-svg-icons';

// components
import CiCampsiteMediaImage from '@/components/CiCampsiteMedia/CiCampsiteMediaImage.vue';
import CiCampsiteMediaPanono from '@/components/CiCampsiteMedia/CiCampsiteMediaPanono.vue';
import CiCampsiteMediaVideo from '@/components/CiCampsiteMedia/CiCampsiteMediaVideo.vue';
import CiSkeletonItem from '@/components/skeleton/CiSkeletonItem.vue';
import CiStickyNavigation from '@/components/CiStickyNavigation/CiStickyNavigation.vue';

type Links = { campsite: number[]; visitor: number[] };

definePageMeta({
  layout: 'default',
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const searchService = useSearchService();
const campsiteStore = useCampsiteStore();
const seoStore = useSeoStore();
const { $gettext, $pgettext } = useGettext();
const { campsite, onKeyUp, isSelected, onMediaItemClicked, mediaItemRefs } = useCampsiteMedia();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { locale } = useI18n();
const { requestAds } = useAds();

const { ppcWebsiteLink } = useCampsiteDetail({
  campsite: () => campsite.value,
});

const { hasAds, isPartOfAvailabilityService, getPreEnterData } = useCampsiteAdsAs({
  campsite: () => campsite.value,
});

const { scrollToRef, sectionRefs } = useCampsiteDetail({
  campsite: () => campsite.value,
});

/********************
 * REFS & VARS       *
 ********************/
const dividerTexts = reactive({
  picture: {
    campsite: '',
    visitor: '',
  },
  video: {
    campsite: '',
    visitor: '',
  },
});

const freezeBottomNavbar = ref(false);
const lastScrollPosition = ref(0);
const links = ref<Links | null>(null);
const prepareStickyNavigationItems = ref([]);
const showBottomNavbar = ref(false);
const subNavigationAnchors = ref([]);

/********************
 * INITIALIZATION    *
 ********************/
if (import.meta.client) {
  await useAsyncData(route.fullPath, getPreEnterData);
}
const { data: loadedData, pending, error } = await useLazyAsyncData(`media-${route.params.identifier}`, async () => {
  try {
    const response = await searchService.getCampsiteMedia(route.params?.identifier as string, locale.value);
    const dividers: any = {};
    const stickyNavigationItems: any[] = [];
    let firstTwoImages: number = 0;
    const hasPpcOrAs
      = (response.purchases ? response.purchases.includes(256) : false) || [
        'ctoutvert',
        'compusoft',
        'camping.info-bvcd',
        'camping.info-booking',
        'camping.care',
        'feratel',
        'phobs',
      ].includes(response.booking_provider);

    /*
      1. campsite pics
      2. panono
      3. campsite vids
      4. visitor pics
      5. visitor vids
    */
    const orders: any = {
      'picture-campsite': 0,
      'panono': 1,
      'video-campsite': 2,
      'picture-visitor': 3,
      'video-visitor': 4,
    };

    response.media.sort((a: any, b: any) => {
      const subtypeA = a.media_type === 'panono' ? '' : `-${a.picture_type || a.video_type}`;
      const subtypeB = b.media_type === 'panono' ? '' : `-${b.picture_type || b.video_type}`;
      const orderKeyA = `${a.media_type}${subtypeA}`;
      const orderKeyB = `${b.media_type}${subtypeB}`;
      const orderA: number = orders[orderKeyA];
      const orderB: number = orders[orderKeyB];

      if (orderA < orderB) {
        return -1;
      } else if (orderA > orderB) {
        return 1;
      }
      return 0;
    });

    response.media.map((item: any, idx: number) => {
      const mediaType = item.media_type;
      const pictureType = item.picture_type;
      const videoType = item.video_type;
      const dividerType = `${mediaType}-${pictureType}-${videoType}`;

      if (mediaType === 'picture') {
        firstTwoImages += 1;
      }

      item.idx = idx;
      item.eager = firstTwoImages < 3;

      if (!dividers[dividerType]) {
        dividers[dividerType] = true;

        if (mediaType !== 'panono') {
          const subType = pictureType || videoType || 'campsite';
          const anchor = `${subType}-${mediaType}`;

          if (idx % 2 === 0) {
            idx++;
          }

          stickyNavigationItems.push({
            anchor,
            label: [mediaType, subType],
          });
        }
      }
      item.selected = false;
      // media.push(item);
      return item;
    });

    response.mediaStructured = {
      campsite: [],
      visitor: [],
    };

    response.media.forEach((item) => {
      if (item.media_type === 'panono') {
        response.mediaStructured['campsite'].push(item);
      } else if (item.media_type === 'picture') {
        if (item.picture_type === 'campsite') {
          response.mediaStructured['campsite'].push(item);
        } else if (item.picture_type === 'visitor') {
          response.mediaStructured['visitor'].push(item);
        }
      } else if (item.media_type === 'video') {
        if (item.video_type === 'campsite') {
          response.mediaStructured['campsite'].push(item);
        } else if (item.video_type === 'visitor') {
          response.mediaStructured['visitor'].push(item);
        }
      }
    });

    const links: Links = {
      campsite: [],
      visitor: [],
    };

    if (hasPpcOrAs) {
      links['campsite'] = getLinksForMedia(response.mediaStructured['campsite'].length);
      links['visitor'] = getLinksForMedia(response.mediaStructured['visitor'].length);
    }

    seoStore.setDynamicMetaParam({
      campsite_name: response.name,
    });

    campsiteStore.campsiteFromMedia = response;

    return {
      campsite: response,
      links,
      stickyNavigationItems,
    };
  } catch (error) {
    fetchError(error);
    return error;
  }
});

if (error.value) {
  throw createError({ statusCode: error.value?.statusCode, message: error.value.statusMessage });
}

onMounted(() => {
  window.addEventListener('scroll', onScroll);
  window.addEventListener('keyup', onKeyUp);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('keyup', onKeyUp);
});

/********************
 * FUNCTIONS         *
 ********************/
watch(() => loadedData.value, setData, { immediate: true });

function setData(newValue) {
  if (newValue) {
    campsite.value = newValue.campsite;
    links.value = newValue.links;
    prepareStickyNavigationItems.value = newValue.stickyNavigationItems;
    setTimeout(() => {
      initStickyNavigation();
    }, 100);

    nextTick(() => {
      // refresh ad slots
      requestAds();
    });
  }
}

function initStickyNavigation() {
  const getReduceFunction = (mediaType, subType) => {
    return (sum, obj) => {
      let n = 0;
      const objSubType = obj.picture_type || obj.video_type || null;
      if (obj.media_type === mediaType && objSubType === subType) {
        n = 1;
      }
      return sum + n;
    };
  };
  const numItems = (mediaType, subType) => campsite.value.media?.reduce(getReduceFunction(mediaType, subType), 0);
  const counts = {
    picture: {
      campsite: numItems('picture', 'campsite') + numItems('panono', null),
      visitor: numItems('picture', 'visitor'),
    },
    video: {
      campsite: numItems('video', 'campsite'),
      visitor: numItems('video', 'visitor'),
    },
  };
  const getNavigationText = (mediaType, subType, withCount = true) => {
    const texts = {
      picture: {
        campsite: $pgettext('Media Sticky navigation - keep this short!', 'Campsite owner photos'),
        visitor: $pgettext('Media Sticky navigation - keep this short!', 'Guests photos'),
      },
      video: {
        campsite: $pgettext('Media Sticky navigation - keep this short!', 'Campsite owner videos'),
        visitor: $pgettext('Media Sticky navigation - keep this short!', 'Guests videos'),
      },
    };
    if (texts[mediaType]) {
      let count = 0;
      if (counts[mediaType]) {
        count = counts[mediaType][subType];
      }
      if (withCount) {
        return (
          `${texts[mediaType][subType]} <span class="ml-1 inline-block rounded-[10rem] bg-primary px-[7px] py-[3px] text-xs font-bold leading-3 text-white">${count}</span>`
        );
      } else {
        return texts[mediaType][subType] || '';
      }
    }
  };

  const navAnchors = [];
  prepareStickyNavigationItems.value.forEach((item) => {
    dividerTexts[item.label[0]][item.label[1]] = getNavigationText(item.label[0], item.label[1], false);
    navAnchors.push({
      anchor: item.anchor,
      label: getNavigationText(item.label[0], item.label[1]),
      ref: sectionRefs[item.anchor],
    });
  });
  subNavigationAnchors.value = navAnchors;
}

function onScroll() {
  // Get the current scroll position
  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
  if (currentScrollPosition < 0) {
    return;
  }
  let scrolledToBottomOffset = 50;
  const footer = document.getElementById('footer');
  if (footer) {
    scrolledToBottomOffset = footer.clientHeight;
  }
  freezeBottomNavbar.value = false;
  if (Math.abs(currentScrollPosition + window.innerHeight - document.body.offsetHeight) <= scrolledToBottomOffset) {
    showBottomNavbar.value = true;
    freezeBottomNavbar.value = true;
    lastScrollPosition.value = currentScrollPosition;
    return;
  }
  if (Math.abs(currentScrollPosition - lastScrollPosition.value) < 60) {
    return;
  }
  // Here we determine whether we need to show or hide the navbar
  showBottomNavbar.value = currentScrollPosition > 150 && currentScrollPosition < lastScrollPosition.value;
  // Set the current scroll position as the last scroll position
  lastScrollPosition.value = currentScrollPosition;
}

function getMediaItemClasses(item) {
  return [isSelected(item) ? 'selected' : 'col-lg-6 group', `media-item--${item.media_type}`];
}

function onScrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function getAdId(idx) {
  const mapping = {
    9: 'camping_content_3',
    13: 'camping_content_4',
    17: 'camping_content_5',
  };
  return mapping[idx] || '';
}

function getLinksForMedia(numMedia: number) {
  let links: any[] = [];
  if (numMedia < 3) {
    links = [numMedia];
  } else {
    links = [4];
    const sample = 1;
    let maxNum = Math.max(1, Math.floor(numMedia / sample));
    let minNum = Math.max(Math.floor(maxNum / 2), 1);
    const numPpc = Math.floor(Math.random() * (maxNum - minNum + 1) + minNum);

    let lastPpcOrAs = 2;
    for (let i = 0; i < numPpc; i++) {
      minNum = lastPpcOrAs + 2;
      maxNum = Math.min(numMedia, minNum + i * Math.floor(numMedia / numPpc));
      lastPpcOrAs
            = Math.min(
          Math.floor(numMedia / 2),
          Math.floor(
            Math.random() * (Math.floor(maxNum / 2) - Math.floor(minNum / 2) + 1) + Math.floor(minNum / 2),
          ),
        ) * 2;
      if (!links.includes(lastPpcOrAs) && !links.includes(lastPpcOrAs - 1) && !links.includes(lastPpcOrAs - 2)) {
        links.push(lastPpcOrAs);
      }
    }
  }
  return links;
}
</script>

<template>
  <div>
    <template v-if="!fetchFailed">
      <template v-if="pending">
        <CiSkeletonItem
          color="#f5f5f5"
          class="h-[114px] border-b-2 border-gray-40"
        />

        <template v-if="hasAds">
          <div class="absolute size-full">
            <div class="container h-full">
              <div class="flex h-full">
                <CiSkeletonItem
                  class="ad ad--skyscraper ad--skyscraper-left sticky top-0 z-10"
                  style="top: 50px"
                />
                <CiSkeletonItem
                  class="ad ad--skyscraper ad--skyscraper-right sticky top-0 z-10"
                  style="top: 50px"
                />
              </div>
            </div>
          </div>

          <div class="container-lg mb-4 mt-2 pt-0">
            <CiSkeletonItem class="ad ad--header" />
          </div>
        </template>

        <div class="container mt-4">
          <div class="row row-small-gutter">
            <template
              v-for="idx in 8"
              :key="`item_${idx}`"
            >
              <div class="media-item col-12 col-lg-6 mb-[10px]">
                <CiSkeletonItem class="relative h-full rounded pt-[66.61%]" />
              </div>

              <template v-if="hasAds && idx === 4">
                <div
                  :key="`ad_${idx}`"
                  class="col-12"
                >
                  <CiSkeletonItem class="ad--h-600-480-570 mb-[10px] w-full" />
                </div>
              </template>
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- back navigation -->
        <div class="bg-gray-10">
          <div class="container py-2">
            <CiNavigationToggle
              type="black"
              :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
            >
              <span class="ml-3 text-dark">{{ $gettext('Back to %{campsite}', { campsite: campsite.name }, true) }}</span>
            </CiNavigationToggle>
          </div>
        </div>

        <!-- sticky navigation -->
        <CiStickyNavigation
          :ref="el => (sectionRefs['subnav'] = el as InstanceType<typeof CiStickyNavigation>)"
          :items="toRaw(subNavigationAnchors)"
          :use-html="true"
          :jump-to-first="false"
          :jump-to-last="true"
          @item-clicked="scrollToRef"
        />

        <div class="relative">
          <div
            v-if="hasAds"
            class="skyscraper absolute size-full pb-12"
          >
            <div class="container h-full">
              <div class="flex h-full">
                <CiAdSlot
                  id="camping_sidebar_left"
                  class="ad--skyscraper ad--skyscraper-left sticky top-0"
                  style="top: 50px; z-index: 31"
                />
                <CiAdSlot
                  id="camping_sidebar_1"
                  class="ad--skyscraper ad--skyscraper-right sticky top-0"
                  style="top: 50px; z-index: 31"
                />
                <!-- won't be rendered -->
                <!-- <ci-ad-slot id="cpdp_wallpaper_pixel"></ci-ad-slot> -->
              </div>
            </div>
          </div>

          <div
            v-if="hasAds"
            class="container-lg mb-4 mt-2 pt-0"
          >
            <CiAdSlot
              id="camping_header"
              class="ad--header"
            />
          </div>

          <!-- media elements -->
          <div
            class="container relative mt-4"
            :class="{ 'mb-32 md:mb-20': !freezeBottomNavbar, 'mb-4': freezeBottomNavbar }"
          >
            <div class="row row-small-gutter">
              <div
                v-if="campsite.mediaStructured?.['campsite'].some((item) => (item.media_type === 'picture'))"
                id="campsite-picture"
                :ref="el => (sectionRefs['campsite-picture'] = el as HTMLElement)"
                class="divider col-12 invisible m-0 size-0 p-0"
              />
              <template v-for="(item, idx) of campsite.mediaStructured?.['campsite']" :key="item.id">
                <template v-if="hasAds && (item.idx + 1) === 5">
                  <div
                    :key="`ad_${item.idx}`"
                    class="col-12 mb-[10px]"
                  >
                    <CiAdSlot
                      id="camping_content_2"
                      class="ad--h-600-480-570"
                    />
                  </div>
                </template>
                <template v-if="hasAds && ((item.idx + 1) === 9 || (item.idx + 1) === 13 || (item.idx + 1) === 17)">
                  <div
                    :key="`ad_${item.idx + 1}`"
                    class="col-12 mb-[10px]"
                  >
                    <CiAdSlot
                      :id="getAdId(item.idx + 1)"
                      class="ad--h-250"
                    />
                  </div>
                </template>

                <div
                  v-if="
                    item.idx !== undefined
                      && item.idx !== null
                      && links.campsite?.includes(idx)
                      && (ppcWebsiteLink('inline') || isPartOfAvailabilityService)
                  "
                  :key="`link-${item.idx}`"
                  class="col-12 website-link mb-6 mt-3 text-center"
                >
                  <div class="flex flex-col justify-center lg:flex-row">
                    <a
                      v-if="ppcWebsiteLink('inline')"
                      :href="ppcWebsiteLink('inline')"
                      class="button w-full md:w-auto"
                      :class="[isPartOfAvailabilityService ? 'button--primary-outline mr-2' : 'button--primary']"
                      referrerpolicy="unsafe-url"
                      target="_blank"
                      rel="noopener"
                    >
                      <span>{{ $gettext('Go to website') }}</span>
                      <CiAwesomeIcon
                        :icon="faChevronRight"
                        ratio="0.6"
                      />
                    </a>
                    <nuxt-link
                      v-if="isPartOfAvailabilityService"
                      class="button button--primary mt-2 lg:mt-0"
                      :to="
                        localePath({
                          name: RBN_CAMPSITE_DETAIL,
                          params: { identifier: route.params.identifier },
                          hash: '#availabilities',
                        })
                      "
                    >
                      <span>{{ $gettext('Check availability') }}</span>
                      <CiAwesomeIcon
                        :icon="faChevronRight"
                        ratio="0.6"
                      />
                    </nuxt-link>
                  </div>
                </div>

                <div
                  v-if="campsite.mediaStructured?.['campsite'].findIndex((item) => (item.media_type === 'video')) === idx"
                  id="campsite-video"
                  :ref="el => (sectionRefs['campsite-video'] = el as HTMLElement)"
                  class="divider invisible m-0 size-0 p-0"
                  :class="{ 'col-12': idx % 2 === 0 }"
                />

                <div
                  :ref="el => (mediaItemRefs[item.idx] = el as HTMLElement)"
                  :data-idx="idx"
                  class="media-item col-12 mb-[10px]"
                  :class="getMediaItemClasses(item)"
                >
                  <CiCampsiteMediaImage
                    v-if="item.media_type === 'picture'"
                    :item="item"
                    :selected="isSelected(item)"
                    :alt-fallback="campsite.name"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiCampsiteMediaPanono
                    v-else-if="item.media_type === 'panono'"
                    :selected="isSelected(item)"
                    :panono-id="item.id"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiCampsiteMediaVideo
                    v-else-if="item.media_type === 'video'"
                    :item="item"
                    :selected="isSelected(item)"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiAwesomeIcon
                    v-if="item.media_type !== 'video'"
                    :icon="faSearchPlus"
                    ratio="2.0"
                    class="zoom-icon absolute fill-white"
                  />

                  <div
                    v-if="item.media_type === 'panono'"
                    class="panono-icon absolute fill-white"
                  >
                    <CiAwesomeIcon
                      :icon="faCube"
                      ratio="2.0"
                    />
                  </div>
                </div>
              </template>
              <div
                v-if="campsite.mediaStructured?.['visitor'].some((item) => (item.media_type === 'picture'))"
                id="visitor-picture"
                :ref="el => (sectionRefs['visitor-picture'] = el as HTMLElement)"
                class="divider col-12"
              >
                <h2
                  class="my-4 text-center"
                >
                  {{ $pgettext('Media Sticky navigation - keep this short!', 'Guests photos') }}
                </h2>
              </div>
              <template v-for="(item, idx) of campsite.mediaStructured?.['visitor']" :key="item.id">
                <template v-if="hasAds && (item.idx + 1) === 5">
                  <div
                    :key="`ad_${item.idx}`"
                    class="col-12 mb-[10px]"
                  >
                    <CiAdSlot
                      id="camping_content_2"
                      class="ad--h-600-480-570"
                    />
                  </div>
                </template>

                <template v-if="hasAds && ((item.idx + 1) === 9 || (item.idx + 1) === 13 || (item.idx + 1) === 17)">
                  <div
                    :key="`ad_${item.idx + 1}`"
                    class="col-12 mb-[10px]"
                  >
                    <CiAdSlot
                      :id="getAdId(item.idx + 1)"
                      class="ad--h-250"
                    />
                  </div>
                </template>
                <div
                  v-if="
                    item.idx !== undefined
                      && item.idx !== null
                      && links['visitor']?.includes(idx)
                      && (ppcWebsiteLink('inline') || isPartOfAvailabilityService)
                  "
                  :key="`link-${item.idx}`"
                  class="col-12 website-link mb-6 mt-3 text-center"
                >
                  <div class="flex flex-col justify-center lg:flex-row">
                    <a
                      v-if="ppcWebsiteLink('inline')"
                      :href="ppcWebsiteLink('inline')"
                      class="button w-full md:w-auto"
                      :class="[isPartOfAvailabilityService ? 'button--primary-outline mr-2' : 'button--primary']"
                      referrerpolicy="unsafe-url"
                      target="_blank"
                      rel="noopener"
                    >
                      <span>{{ $gettext('Go to website') }}</span>
                      <CiAwesomeIcon
                        :icon="faChevronRight"
                        ratio="0.6"
                      />
                    </a>
                    <nuxt-link
                      v-if="isPartOfAvailabilityService"
                      class="button button--primary mt-2 lg:mt-0"
                      :to="
                        localePath({
                          name: RBN_CAMPSITE_DETAIL,
                          params: { identifier: route.params.identifier },
                          hash: '#availabilities',
                        })
                      "
                    >
                      <span>{{ $gettext('Check availability') }}</span>
                      <CiAwesomeIcon
                        :icon="faChevronRight"
                        ratio="0.6"
                      />
                    </nuxt-link>
                  </div>
                </div>

                <div
                  v-if="campsite.mediaStructured?.['visitor'].findIndex((item) => (item.media_type === 'video')) === idx"
                  id="visitor-video"
                  :ref="el => (sectionRefs['visitor-video'] = el as HTMLElement)"
                  class="divider invisible m-0 size-0 p-0"
                  :class="{ 'col-12': idx % 2 === 0 }"
                />

                <div
                  :ref="el => (mediaItemRefs[item.idx] = el as HTMLElement)"
                  :data-idx="idx"
                  class="media-item col-12 mb-[10px]"
                  :class="getMediaItemClasses(item)"
                >
                  <CiCampsiteMediaImage
                    v-if="item.media_type === 'picture'"
                    :item="item"
                    :selected="isSelected(item)"
                    :alt-fallback="campsite.name"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiCampsiteMediaPanono
                    v-else-if="item.media_type === 'panono'"
                    :selected="isSelected(item)"
                    :panono-id="item.id"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiCampsiteMediaVideo
                    v-else-if="item.media_type === 'video'"
                    :item="item"
                    :selected="isSelected(item)"
                    @click="onMediaItemClicked($event, item, 50)"
                  />

                  <CiAwesomeIcon
                    v-if="item.media_type !== 'video'"
                    :icon="faSearchPlus"
                    ratio="2.0"
                    class="zoom-icon absolute fill-white"
                  />

                  <div
                    v-if="item.media_type === 'panono'"
                    class="panono-icon absolute fill-white"
                  >
                    <CiAwesomeIcon
                      :icon="faCube"
                      ratio="2.0"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- bottom nav bar -->
          <transition name="fade">
            <div
              v-if="showBottomNavbar"
              class="navbar-bottom bottom-[15px] left-0 z-10 flex w-full flex-wrap justify-center px-2 md:flex-nowrap"
              :class="{ 'fixed': !freezeBottomNavbar, 'mb-6': freezeBottomNavbar }"
            >
              <nuxt-link
                :to="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
                class="button button--primary-outline-white mb-2 w-full md:mb-0 md:mr-2 md:w-auto"
              >
                <CiAwesomeIcon
                  :icon="faChevronLeft"
                  ratio="0.6"
                />
                <span class="pr-2">{{ $gettext('Back to campsite') }}</span>
              </nuxt-link>
              <a
                href="#"
                class="button button--primary-outline-white w-full md:w-auto"
                referrerpolicy="unsafe-url"
                target="_blank"
                rel="noopener"
                @click.prevent="onScrollToTop"
              >
                <span>{{ $gettext('Back to top') }}</span>
                <CiAwesomeIcon
                  :icon="faArrowUp"
                  ratio="0.6"
                />
              </a>
            </div>
          </transition>
        </div>
      </template>
    </template>
    <CiErrorView
      v-else
      :error="fetchStatus"
    />
  </div>
</template>

<style lang="scss" scoped>
.media-item {
  transition: filter 0.5s ease-out;

  .zoom-icon,
  .panono-icon {
    @apply pointer-events-none left-1/2 top-1/2 opacity-0;
    transform: translate(-50%, -50%);
    transition: opacity 0.5s ease-out;
  }

  .panono-icon {
    @apply rounded-xl bg-black/70 p-5 opacity-0;
    @media (hover: none) and (pointer: coarse) {
      display: none;
    }
  }

  &:not(.selected) {
    .panono-icon {
      @apply opacity-80;
    }
  }

  .no-touch &:hover {
    @apply cursor-pointer;

    .panono-icon {
      @apply opacity-0;
    }

    &:not(.selected) {
      filter: brightness(70%);

      .zoom-icon {
        @apply opacity-100;
      }
    }
  }
}
</style>
